import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material';
import JobDeliveryPhotos from './JobDeliveryPhotos';
import api from '../services/api';
import { googleMapsQueryUrl, formatJobDeliveryLine } from '../utils/jobMapsLink';
import { formatJobName } from '../utils/jobFormFromPrevious';
import { formatFuelQuantities } from '../utils/formatFuelQuantities';
import { getJobEquipmentCount } from '../utils/invoiceDeliveryNotes';

function formatPaymentStatusLabel(status) {
  if (!status) return 'Unknown';
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getPaymentStatusColor(status) {
  const colors = {
    paid: 'success',
    partial: 'warning',
    overdue: 'error',
    pending: 'info',
    not_submitted: 'default',
  };
  return colors[status] || 'default';
}

function formatJobStatus(status) {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
}

function SubJobsSection({ completion }) {
  const subJobs = completion?.sub_jobs;
  if (!Array.isArray(subJobs) || subJobs.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'info.main' }}>
        Sub deliveries ({subJobs.length})
      </Typography>
      {subJobs.map((subJob, index) => {
        const subJobAddress = [
          subJob.delivery_address,
          subJob.delivery_city,
          subJob.delivery_state,
          subJob.delivery_zip,
        ]
          .filter(Boolean)
          .join(', ');

        return (
          <Box
            key={subJob.id || index}
            sx={{
              mb: 2,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Stop {index + 1}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Address:</strong>{' '}
              {subJobAddress ? (
                <Link
                  href={googleMapsQueryUrl(subJobAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  {subJobAddress}
                </Link>
              ) : (
                'N/A'
              )}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Delivered:</strong>{' '}
              {formatFuelQuantities({
                diesel_quantity: subJob.diesel_delivered,
                def_quantity: subJob.def_delivered,
                dyed_quantity: subJob.dyed_delivered,
              })}
            </Typography>
            {subJob.delivery_notes?.trim() && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                <strong>Notes:</strong> {subJob.delivery_notes}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default function CompletedJobDetailsDialog({
  open,
  onClose,
  job: initialJob = null,
  jobId = null,
  isMobile = false,
  canAddPhotos = false,
  onJobUpdated = null,
}) {
  const [job, setJob] = useState(initialJob);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setError('');
      return;
    }

    setJob(initialJob ?? null);

    const id = jobId || initialJob?.id;
    if (!id) return undefined;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/jobs/${id}/`);
        if (!cancelled) setJob(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              err.response?.data?.detail ||
              'Failed to load job details.'
          );
          if (initialJob) setJob(initialJob);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, jobId, initialJob]);

  const displayJob = job ?? initialJob;
  if (!open) return null;

  const address = displayJob ? formatJobDeliveryLine(displayJob) || 'N/A' : '';
  const completion = displayJob?.delivery_completion;
  const equipmentCount = getJobEquipmentCount(displayJob);
  const phone = displayJob?.customer_phone_number;
  const phoneHref = phone ? `tel:${String(phone).replace(/[^\d+]/g, '')}` : null;

  const handlePhotosAdded = (updatedJob) => {
    setJob(updatedJob);
    if (onJobUpdated) onJobUpdated(updatedJob);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{ '& .MuiDialog-paper': { m: { xs: 0, sm: 2 } } }}
    >
      <DialogTitle>
        {displayJob ? formatJobName(displayJob) : 'Job details'}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : !displayJob ? (
          <Typography variant="body2" color="text.secondary">
            Job not found.
          </Typography>
        ) : (
          <Box sx={{ mt: 0.5 }}>
            {error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {(displayJob.customer_name || phone) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Customer
                </Typography>
                {displayJob.customer_name && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Company:</strong> {displayJob.customer_name}
                  </Typography>
                )}
                {phone && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Phone:</strong>{' '}
                    <Link href={phoneHref} underline="hover">
                      {phone}
                    </Link>
                  </Typography>
                )}
              </Box>
            )}

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Job information
            </Typography>
            {displayJob.job_status && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Status:</strong>{' '}
                <Chip
                  label={formatJobStatus(displayJob.job_status)}
                  size="small"
                  sx={{ verticalAlign: 'middle' }}
                />
              </Typography>
            )}
            {displayJob.job_name?.trim() && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Job name:</strong> {displayJob.job_name}
              </Typography>
            )}
            {displayJob.frequency && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Frequency:</strong> {displayJob.frequency}
              </Typography>
            )}
            {displayJob.scheduled_date && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Scheduled:</strong> {new Date(displayJob.scheduled_date).toLocaleString()}
              </Typography>
            )}
            {displayJob.created_at && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Created:</strong> {new Date(displayJob.created_at).toLocaleString()}
              </Typography>
            )}
            {displayJob.driver_name && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Driver:</strong> {displayJob.driver_name}
              </Typography>
            )}
            {displayJob.vehicle_info && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Vehicle:</strong> {displayJob.vehicle_info}
              </Typography>
            )}

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
              Delivery information
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Location:</strong>{' '}
              {address !== 'N/A' ? (
                <Link
                  href={googleMapsQueryUrl(address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  {address}
                </Link>
              ) : (
                'N/A'
              )}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-wrap' }}>
              <strong>Delivery notes:</strong>{' '}
              {displayJob.delivery_notes?.trim() ? displayJob.delivery_notes : '—'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Requested:</strong>{' '}
              {formatFuelQuantities({
                diesel_quantity: displayJob.diesel_quantity,
                def_quantity: displayJob.def_quantity,
                dyed_quantity: displayJob.dyed_quantity,
              }) || 'N/A'}
            </Typography>
            {(Number(displayJob.delivered_diesel) > 0 ||
              Number(displayJob.delivered_def) > 0 ||
              Number(displayJob.delivered_dyed) > 0) && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Delivered:</strong>{' '}
                {formatFuelQuantities({
                  diesel_quantity: displayJob.delivered_diesel,
                  def_quantity: displayJob.delivered_def,
                  dyed_quantity: displayJob.delivered_dyed,
                })}
              </Typography>
            )}
            {equipmentCount != null && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Equipment count:</strong> {equipmentCount}
              </Typography>
            )}
            {displayJob.completed_at && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Completed:</strong> {new Date(displayJob.completed_at).toLocaleString()}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Payment status:</strong>{' '}
              <Chip
                label={formatPaymentStatusLabel(displayJob.payment_status)}
                color={getPaymentStatusColor(displayJob.payment_status)}
                size="small"
                sx={{ verticalAlign: 'middle' }}
              />
            </Typography>
            {displayJob.invoice_number && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Invoice:</strong> {displayJob.invoice_number}
              </Typography>
            )}

            {completion && displayJob.job_status === 'complete' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Completion record
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Diesel delivered:</strong> {completion.diesel_delivered || 0} gal
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>DEF delivered:</strong> {completion.def_delivered || 0} gal
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Dyed delivered:</strong> {completion.dyed_delivered || 0} gal
                </Typography>
                {completion.equipment_count != null && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Equipment count:</strong> {completion.equipment_count}
                  </Typography>
                )}
                {completion.completion_notes && (
                  <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-wrap' }}>
                    <strong>Driver notes:</strong> {completion.completion_notes}
                  </Typography>
                )}
              </Box>
            )}

            <SubJobsSection completion={completion} />

            {displayJob.job_status === 'complete' && (
              <JobDeliveryPhotos
                photos={completion?.photos}
                jobId={displayJob.id}
                canAdd={canAddPhotos}
                onPhotosAdded={handlePhotosAdded}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
